import { useMutation, useQuery } from '@apollo/client';
import {
  Door,
  Mutation,
  MutationAssignToAccessGrantsArgs,
  MutationUnassignFromAccessGrantsArgs,
  MutationUpdateDoorArgs,
  Query,
  UpdateDoorOfficeModeInput
} from 'graphql/generated/graphqlTypes';
import {
  ASSIGN_DEVICE_TO_ACCESS_GRANTS,
  ASSIGN_DOOR_TO_LOCATIONS,
  UNASSIGN_DEVICE_FROM_ACCESS_GRANTS,
  UPDATE_DOOR_INFO
} from 'graphql/v2/mutations';
import {
  GET_DOORS_FOR_TENANT,
  GET_LOCATIONS_WITH_DOORS_FOR_TENANT,
  GET_VISITORS_GROUPS_DEVICE_DETAILS
} from 'graphql/v2/queries';
import { useEffect, useMemo } from 'react';
import { getLockByLockId } from 'utils/Lock/Lock';
import {
  getAllAccessGrantsOfVisitorGroup,
  getVisitorGroupById,
  getVisitorGroupsThatHaveLockAssignedDirectly
} from 'utils/VisitorGroup/VisitorGroup';
import { stitchDoorLocations } from 'v2/utils/lock';
import { TranslatedHookProps } from '../hooks.types';

interface Props extends TranslatedHookProps {
  deviceId: string;
}

function diffIds(prev: string[], next: string[]) {
  const prevSet = new Set(prev);
  const nextSet = new Set(next);

  const added = next.filter((id) => !prevSet.has(id));
  const removed = prev.filter((id) => !nextSet.has(id));

  return { added, removed };
}

const useDeviceDetails = ({ translation, handleFetchError, deviceId }: Props) => {
  const {
    loading: doorsLoading,
    error: doorsError,
    data: doors,
    refetch: refetchDoors
  } = useQuery<Pick<Query, 'getDoorsForTenant'>>(GET_DOORS_FOR_TENANT);

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(doorsError);
    }
  }, [doorsError]);

  const {
    loading: locationsLoading,
    error: locationsError,
    data: locations,
    refetch: refetchLocations
  } = useQuery<Pick<Query, 'getLocationsForTenant'>>(GET_LOCATIONS_WITH_DOORS_FOR_TENANT);

  useEffect(() => {
    if (locationsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(locationsError);
    }
  }, [locationsError]);

  const {
    loading: visitorGroupsLoading,
    error: visitorGroupsError,
    data: visitorGroups
  } = useQuery<Pick<Query, 'getVisitorGroupsForTenant'>>(GET_VISITORS_GROUPS_DEVICE_DETAILS);

  useEffect(() => {
    if (visitorGroupsError) {
      handleFetchError(translation.devices_error_assigning);
      console.error(visitorGroupsError);
    }
  }, [visitorGroupsError]);

  const [updateDoorInfoMutation, { loading: updateDoorInfoLoading }] = useMutation<
    Pick<Mutation, 'updateDoor'>,
    MutationUpdateDoorArgs
  >(UPDATE_DOOR_INFO, {
    refetchQueries: (result) => [
      { query: GET_DOORS_FOR_TENANT, variables: { filter: { id: result.data?.updateDoor?.id } } },
      GET_LOCATIONS_WITH_DOORS_FOR_TENANT
    ],
    awaitRefetchQueries: true
  });

  const updateDoorInfo = async (
    name: string,
    doorId: string,
    officeModeInput: UpdateDoorOfficeModeInput | undefined
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    try {
      const result = await updateDoorInfoMutation({
        variables: { doorId, name, officeMode: officeModeInput }
      });
      const doorInfo = result.data?.updateDoor?.id;
      if (!doorInfo) return { success: false, errorMessage: translation.error_while_updating_device };
      return { success: true };
    } catch (err) {
      return { success: false, errorMessage: err instanceof Error ? err.message : translation.an_error_occurred };
    }
  };

  const [assignDoorToLocationsMutation, { loading: assignDoorToLocationsLoading }] = useMutation<
    Pick<Mutation, 'updateDoor'>,
    MutationUpdateDoorArgs
  >(ASSIGN_DOOR_TO_LOCATIONS, {
    refetchQueries: (result) => [
      { query: GET_DOORS_FOR_TENANT, variables: { filter: { id: result.data?.updateDoor?.id } } },
      GET_LOCATIONS_WITH_DOORS_FOR_TENANT
    ],
    awaitRefetchQueries: true
  });

  const [
    assignDoorToAccessGrantMutation,
    { loading: assignDoorToAccessGrantLoading, error: assignDoorToAccessGrantError }
  ] = useMutation<Pick<Mutation, 'assignToAccessGrants'>, MutationAssignToAccessGrantsArgs>(
    ASSIGN_DEVICE_TO_ACCESS_GRANTS,
    {
      refetchQueries: [GET_DOORS_FOR_TENANT, GET_VISITORS_GROUPS_DEVICE_DETAILS],
      awaitRefetchQueries: true
    }
  );

  useEffect(() => {
    if (assignDoorToAccessGrantError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(assignDoorToAccessGrantError);
    }
  }, [assignDoorToAccessGrantError]);

  const [
    unassignDoorFromAccessGrantMutation,
    { loading: unassignDoorFromAccessGrantLoading, error: unassignDoorFromAccessGrantError }
  ] = useMutation<Pick<Mutation, 'unassignFromAccessGrants'>, MutationUnassignFromAccessGrantsArgs>(
    UNASSIGN_DEVICE_FROM_ACCESS_GRANTS,
    {
      refetchQueries: [GET_DOORS_FOR_TENANT, GET_VISITORS_GROUPS_DEVICE_DETAILS],
      awaitRefetchQueries: true
    }
  );

  useEffect(() => {
    if (unassignDoorFromAccessGrantError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(unassignDoorFromAccessGrantError);
    }
  }, [unassignDoorFromAccessGrantError]);

  const assignDoorToDoorGroupOrVisitorGroup = async (
    door: Door,
    doorGroupIds: string[],
    visitorGroupIds: string[]
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    try {
      const result = await assignDoorToLocationsMutation({
        variables: { doorId: door.id, name: door.name, locationIds: doorGroupIds }
      });
      const doorId = result.data?.updateDoor?.id;
      if (!doorId) return { success: false, errorMessage: translation.error_while_updating_device };
      const accessGrantsIdsBeforeAssigning = getVisitorGroupsThatHaveLockAssignedDirectly(
        visitorGroups?.getVisitorGroupsForTenant || [],
        deviceId
      ).flatMap((visitorGroup) =>
        getAllAccessGrantsOfVisitorGroup(visitorGroup).flatMap((accessGrant) => accessGrant.id)
      );
      const visitorGroupsToAssign = visitorGroupIds
        .map((visitorGroupId) => getVisitorGroupById(visitorGroups?.getVisitorGroupsForTenant || [], visitorGroupId))
        .filter((visitorGroup) => visitorGroup !== undefined);

      const accessGrantsIdsToAssign = visitorGroupsToAssign.flatMap((visitorGroup) =>
        getAllAccessGrantsOfVisitorGroup(visitorGroup).flatMap((accessGrant) => accessGrant.id)
      );
      const { added, removed } = diffIds(accessGrantsIdsBeforeAssigning, accessGrantsIdsToAssign);
      if (added.length > 0)
        await assignDoorToAccessGrantMutation({
          variables: { doorIds: [doorId], accessGrantIds: added, locationIds: [] }
        });
      if (removed.length > 0)
        await unassignDoorFromAccessGrantMutation({
          variables: { doorIds: [doorId], accessGrantIds: removed, locationIds: [] }
        });
      return { success: true };
    } catch (err) {
      return { success: false, errorMessage: err instanceof Error ? err.message : translation.an_error_occurred };
    }
  };

  const stitchedDoors = useMemo(
    () => stitchDoorLocations(doors?.getDoorsForTenant ?? [], locations?.getLocationsForTenant ?? []),
    [doors, locations]
  );

  return {
    device: getLockByLockId(stitchedDoors, deviceId),
    deviceLoading: doorsLoading,
    hasDeviceFetchError: Boolean(doorsError || locationsError),
    retryFetchingDevice: () => {
      refetchDoors();
      refetchLocations();
    },
    locations: locations?.getLocationsForTenant,
    locationsLoading,
    visitorGroups: visitorGroups?.getVisitorGroupsForTenant,
    visitorGroupsLoading,
    updateDoorInfo,
    updateDoorInfoLoading,
    assignDoorToDoorGroupOrVisitorGroup,
    assignDoorToDoorGroupOrVisitorGroupLoading:
      assignDoorToLocationsLoading || assignDoorToAccessGrantLoading || unassignDoorFromAccessGrantLoading
  };
};

export default useDeviceDetails;
